import { useMemo } from "react"
import { EmailIntegration } from "@/schema"

import { useFetchCurrentUser } from "@/hooks/api/users"

import { useEmailIntegrations } from "./queries"

/**
 * Hook to get email integrations configured for sending emails.
 * Returns the default integration to use and all sending-capable integrations.
 */
export function useEmailSendingIntegration() {
  const { data: emailIntegrations, ...queryState } = useEmailIntegrations()
  const {
    data: currentUser,
    isLoading: isCurrentUserLoading,
    isFetching: isCurrentUserFetching,
    isSuccess: isCurrentUserSuccess,
    isError: isCurrentUserError,
    refetch: refetchCurrentUser,
    isFetchedAfterMount: isCurrentUserFetchedAfterMount,
  } = useFetchCurrentUser({ staleTime: 0 })
  const currentUserId = currentUser?.id

  const accessibleEmailIntegrations = useMemo(
    () => currentUser?.accessible_email_integrations ?? emailIntegrations ?? [],
    [currentUser?.accessible_email_integrations, emailIntegrations]
  )

  const sendingIntegrations = useMemo<EmailIntegration[]>(() => {
    const integrationPriority = (integration: EmailIntegration) => {
      const isOwnedByCurrentUser = integration.user.id === currentUserId

      if (isOwnedByCurrentUser && integration.is_primary) return 0
      if (isOwnedByCurrentUser) return 1
      if (integration.is_primary) return 2
      return 3
    }

    return [...accessibleEmailIntegrations]
      .filter((i) => i.sync_emails && i.access_level === "read_and_send")
      .sort((a, b) => integrationPriority(a) - integrationPriority(b))
  }, [accessibleEmailIntegrations, currentUserId])

  const defaultSendingIntegration = useMemo<EmailIntegration | null>(() => {
    if (!sendingIntegrations.length) return null

    // Priority order:
    // 1. Current user's primary integration
    // 2. Current user's first sendable integration
    // 3. Shared primary integration
    // 4. First shared sendable integration
    return sendingIntegrations[0] || null
  }, [sendingIntegrations])

  return {
    ...queryState,
    currentUser,
    isSenderError: queryState.isError || isCurrentUserError,
    refetchSender: () => Promise.all([queryState.refetch(), refetchCurrentUser()]),
    isSenderReady:
      isCurrentUserFetchedAfterMount && isCurrentUserSuccess && !isCurrentUserFetching && queryState.isSuccess,
    defaultSendingIntegration,
    sendingIntegrations,
    emailIntegrations: accessibleEmailIntegrations,
    isLoading: queryState.isLoading || isCurrentUserLoading,
  }
}
